// An AI workout draft (import from a link or a photo) becomes the same ImportDraft the text import produces, so it
// opens in the same review screen and then in the builder. The model only extracts; matching the names to the
// library stays offline (matchImportName), exactly as for pasted text. Missing numbers get a default and a plain
// "assumed" note, so the person sees what to check.
import { matchImportName, type ImportDraft, type ImportExercise, type ImportSection } from '../import/parseText';
import { EXERCISES } from '../seed';
import type { Amount, Exercise, SectionFormat } from '../types';
import type { AiWorkout, AiWorkoutExercise, AiWorkoutSection } from './contract';

function amountOf(e: AiWorkoutExercise): Amount | undefined {
  const perSide = e.perSide ? { perSide: true } : {};
  if (e.amountKind === 'reps' && e.reps > 0) return { kind: 'reps', reps: e.reps, ...(e.repsMax > e.reps ? { repsMax: e.repsMax } : {}), ...perSide };
  if (e.amountKind === 'time' && e.seconds > 0) return { kind: 'time', seconds: e.seconds, ...perSide };
  return undefined;
}

function formatOf(s: AiWorkoutSection, assumed: string[]): SectionFormat {
  switch (s.format) {
    case 'circuit': {
      const rounds = s.rounds || 1;
      if (!s.rounds) assumed.push('1 round assumed');
      const allHaveAmounts = s.exercises.every((e) => amountOf(e));
      const workSec = s.workSec || 40;
      if (!s.workSec && !allHaveAmounts) assumed.push('40 s per exercise assumed where no amount is given');
      return { kind: 'circuit', rounds, workSec, restSec: s.restSec, ...(s.roundRestSec ? { roundRestSec: s.roundRestSec } : {}) };
    }
    case 'sets': {
      const fromExercise = s.exercises.find((e) => e.sets > 0)?.sets ?? 0;
      const sets = s.sets || fromExercise || 3;
      if (!s.sets && !fromExercise) assumed.push('3 sets assumed');
      const restSec = s.restSec || 60;
      if (!s.restSec) assumed.push('60 s rest between sets assumed');
      return { kind: 'sets', sets, restSec };
    }
    case 'emom':
    case 'amrap':
    case 'continuous': {
      const fallback = s.format === 'continuous' ? 20 : 10;
      if (!s.minutes) assumed.push(`${fallback} minutes assumed`);
      return { kind: s.format, minutes: s.minutes || fallback };
    }
    default:
      return { kind: 'list' };
  }
}

function exerciseOf(e: AiWorkoutExercise, format: SectionFormat, exercises: Exercise[]): ImportExercise {
  const m = matchImportName(e.name, exercises);
  const amount = amountOf(e);
  // Sets inside another format stay visible as a note, as in the text import.
  const setsNote = e.sets > 0 && format.kind !== 'sets' ? `${e.sets} ${e.sets === 1 ? 'set' : 'sets'}` : '';
  const note = [setsNote, e.note].filter(Boolean).join(', ').slice(0, 120);
  return {
    name: e.name,
    ...(amount ? { amount } : {}),
    ...(e.sets > 0 && format.kind === 'sets' ? { sets: e.sets } : {}),
    ...(note ? { note } : {}),
    status: m.status,
    ...(m.exerciseId ? { exerciseId: m.exerciseId } : {}),
    candidates: m.candidates,
    by: 'matcher',
    ...(e.unsure ? { unsure: true } : {}),
  };
}

/** The review draft for an AI workout; names matched offline against the given library. */
export function importDraftFromAi(ai: AiWorkout, options: { exercises?: Exercise[] } = {}): ImportDraft {
  const exercises = options.exercises ?? EXERCISES;
  const sections: ImportSection[] = ai.sections.map((s) => {
    const assumed: string[] = [];
    const format = formatOf(s, assumed);
    return { kind: s.kind, format, assumed, exercises: s.exercises.map((e) => exerciseOf(e, format, exercises)) };
  });
  return {
    ...(ai.title ? { title: ai.title } : {}),
    sections,
    ignoredLines: 0,
    ignored: [],
    ...(ai.unsure.length ? { notes: ai.unsure } : {}),
  };
}
